<template>
  <SimplePage v-if="confirmed" :loading="busy">
    <p class="confirmed">{{ $t('reserve/confirm/complete') }}</p>
  </SimplePage>
  <SimplePage v-else :loading="busy" :lang-selector="!isComplete && setting.lang.en">
    <p class="desc">
      <template>{{ $t(mode === 'confirm' ? 'reserve/confirm' : 'reserve/info') }}</template>
      <template v-if="isComplete">{{ $t('reserve/complete') }}</template>
    </p>
    <dl ref="reservation" class="reservation">
      <dt>{{ isTakeOut ? $t('reserve/receiptDateTime') : $t('reserve/datetime') }}</dt>
      <dd>
        <b>{{ $d(new Date(reserveDatetime.date), 'short') }} {{ reserveDatetime.time }}〜</b>
      </dd>
      <template v-if="!isTakeOut">
        <hr />
        <dt>{{ $t('reserve/number') }}</dt>
        <dd>
          <b>{{ $tc('reserve/number/count/confirm', detail.adults + detail.children) }}</b>
        </dd>
      </template>
      <template v-if="setting.displayTableType && !isTakeOut">
        <hr />
        <dt>{{ $t('reserve/seat') }}</dt>
        <dd>
          <b>{{ $t(`reserve/smokingTypes[${detail.smokingType}]`) }}</b>
        </dd>
      </template>
      <template v-if="detail.gnaviCourses.length || detail.courses.length">
        <hr />
        <dt>{{ isTakeOut ? $t('reserve/menu') : $t('reserve/course/reserve') }}</dt>
        <dd v-for="course in detail.gnaviCourses" :key="course.id">
          <b>{{ course.name }}</b>
          <template v-if="course.price && !onlySeat(course)">
            <b> / </b>
            <b :class="{ discount: course.price.discounted || course.price.discountedTaxIn }">
              {{ formatPrice(course.price.fixed, course.price.fixedTaxIn) }}</b
            >
            <b v-if="course.price.discounted || course.price.discountedTaxIn">
              → {{ formatPrice(course.price.discounted, course.price.discountedTaxIn) }}
            </b>
          </template>
        </dd>
        <dd v-for="course in detail.courses" :key="course.id" class="course">
          <b
            >{{ $i18n.choice(course.names) }}
            {{ course.price ? ` / ${$n(course.price, 'currency')} ` : '' }}
            {{ course.tax ? ` (${$t(`tax[${course.tax}]`)})` : '' }}
            <template v-if="isTakeOut">
              <br />
              {{ `${$t('reserve/qty')} : ${$n(course.count)}` }}
            </template>
          </b>
        </dd>
      </template>
      <hr />
      <dt>{{ $t('reserve/name') }}</dt>
      <dd>
        <b>{{ displayName }}</b>
      </dd>
      <hr />
      <template v-if="!!setting.otherTxt.displayFlg && detail.otherTxt">
        <dt>{{ $t('reserve/other') }}</dt>
        <dd class="other">
          <b>{{ detail.otherTxt }}</b>
        </dd>
        <hr />
      </template>
      <dt>{{ $t('store/info') }}</dt>
      <dd>
        <b>{{ $i18n.choice(store.name) }}</b>
        <br />
        <a v-if="mode !== 'confirm'" :href="`tel:${store.tel}`" class="only-sp">{{ store.tel }}</a>
        <span v-else class="only-sp">{{ store.tel }}</span>
        <span class="tel">{{ store.tel }}</span>
        <br />
        {{ $i18n.choice(store.address) }}
        <br />
        <a :href="store.url" target="_blank" @click="shopLink()">{{ store.url }}</a>
      </dd>
      <hr />
      <dt>{{ $t('reserve/rsvId') }}</dt>
      <dd>
        <b>{{ detail.rsvId }}</b>
      </dd>
    </dl>
    <div v-if="mode === 'confirm'" class="confirm">
      <p>{{ $t('reserve/nochange/desc') }}</p>
      <Button primary :disabled="detail.confirmFlg" @click="sendConfirm">
        {{ $t('reserve/nochange') }}
        <template v-if="detail.confirmFlg && $i18n.locale === 'ja'">
          <br />
          ({{ $t('sent') }})
        </template>
      </Button>
      <p>{{ $t('reserve/change/contact') }}</p>
      <b class="tel"><TelIcon />{{ store.tel }}</b>
      <Button class="only-sp" :href="`tel:${store.tel}`"><TelIcon class="tel-icon" />{{ store.tel }}</Button>
      <p>{{ $t('reserve/cancel/warning') }}</p>
    </div>
    <div v-else class="buttons">
      <Button
        small
        :href="calendarUrl"
        target="_blank"
        class="only-sp"
        :icon="CalendarAddIcon"
        @click="event('daicho_reserve_cal')"
      >
        {{ $t('reserve/addCalendarButton') }}
      </Button>
      <Button small :href="mapUrl" target="_blank" :icon="MapIcon" @click="event('daicho_reserve_shop')">
        {{ $t('reserve/mapButton') }}
      </Button>
      <Button
        small
        :href="lineUrl"
        target="_blank"
        class="only-sp"
        :icon="LineIcon"
        @click="event('daicho_reserve_line')"
      >
        {{ $t('reserve/lineButton') }}
      </Button>
      <Button small :icon="CopyIcon" @click="textCopy">
        {{ $t('reserve/copyButton') }}
      </Button>
    </div>
    <Dialog v-if="copyDialog" :message="$t('reserve/copied')" @close="copyDialog = false" />
  </SimplePage>
</template>

<script>
import { mapState } from 'vuex'
import { format, addMinutes, parse } from 'date-fns'
import qs from 'querystring'
import { event } from '@/utils/analytics'
import { toHourClock } from '@/utils/date'
import SimplePage from '@/components/templates/SimplePage'
import Dialog from '@/components/molecules/Dialog'
import Button from '@/components/atoms/Button'
import CalendarAddIcon from '@/components/icons/calendarAdd'
import CopyIcon from '@/components/icons/copy'
import MapIcon from '@/components/icons/map'
import TelIcon from '@/components/icons/tel'
import LineIcon from '@/components/icons/line'

export default {
  name: 'Reservation',
  components: {
    SimplePage,
    Dialog,
    Button,
    TelIcon,
  },
  props: {
    token: { type: String, required: true },
  },
  data() {
    return {
      CalendarAddIcon,
      CopyIcon,
      MapIcon,
      TelIcon,
      LineIcon,
      confirmed: false,
      copyDialog: false,
    }
  },
  computed: {
    ...mapState({
      reception: state => state.reception,
      reservation: state => state.reservation,
      isComplete: state => state.reservation.edit.complete,
      setting: state => state.reception.setting,
    }),
    busy() {
      return this.reception.busy || this.reservation.busy
    },
    mode() {
      return this.$route.name
    },
    store() {
      return this.setting.store
    },
    detail() {
      return this.reservation.detail
    },
    name() {
      const customer = this.detail.customer
      return {
        lastName: customer.lastName || '',
        firstName: customer.firstName || '',
        lastNameKana: customer.lastNameKana || '',
        firstNameKana: customer.firstNameKana || '',
      }
    },
    displayName() {
      const kana =
        this.name.lastNameKana && this.name.firstNameKana && `(${this.name.lastNameKana} ${this.name.firstNameKana})`
      return this.$i18n.locale === 'ja'
        ? `${this.name.lastName} ${this.name.firstName}${kana} 様`
        : `${this.name.lastName} ${this.name.firstName}`
    },
    reserveDatetime() {
      return toHourClock(this.detail.reserveDatetime)
    },
    mapUrl() {
      return `https://maps.google.co.jp/maps?q=${encodeURIComponent(this.$i18n.choice(this.store.address))}`
    },
    detailText() {
      const courses = this.detail.courses
      let detailText = ''
      if (courses.length) {
        if (this.isTakeOut) {
          detailText = courses.map(o => `${this.$i18n.choice(o.names)} ${this.$t('reserve/qty')} : ${this.$n(o.count)}`)
        } else {
          detailText = this.$i18n.choice(courses[0].names)
        }
      }

      return [
        this.isTakeOut ? '' : [`${this.detail.adults + this.detail.children}名`],
        this.displayName,
        Array.isArray(detailText) ? detailText.join('\n') : detailText,
        this.$i18n.choice(this.store.name),
        this.store.tel,
        this.$i18n.choice(this.store.address),
        this.store.url,
        this.detail.rsvId,
      ]
        .filter(v => v)
        .join('\n')
    },
    calendarUrl() {
      const startDate = this.formatUTC(this.detail.reserveDatetime)
      const endDate = this.formatUTC(this.detail.endReserveDatetime)

      return `http://www.google.com/calendar/event?${qs.stringify({
        action: 'TEMPLATE',
        text: this.$i18n.choice(this.store.name),
        dates: `${startDate}/${endDate}`,
        location: this.$i18n.choice(this.store.address),
        details: `${this.$d(parse(this.detail.reserveDatetime), 'long')}\n${this.detailText}`,
      })}`
    },
    lineUrl() {
      const path = this.$router.resolve({ name: 'reservation' }).href
      return `http://line.me/R/msg/text/?${encodeURIComponent(
        `${location.origin}${path}\n${this.$d(parse(this.reserveDatetime.date), 'short')} ${this.reserveDatetime.time}
        \n${this.detailText}`,
      )}`
    },
    isTakeOut() {
      return this.detail.type === 7
    },
  },
  async mounted() {
    if (await this.$store.dispatch('reservation/get', this.token)) {
      this.$i18n.locale = this.detail.lang
    }
  },
  methods: {
    event,
    formatPrice(taxOut, taxIn) {
      return `${this.$n(taxOut || taxIn)}円 (${this.$t(`tax[${taxIn ? 1 : 2}]`)}${
        taxOut && taxIn ? `${this.$n(taxIn)}円` : ''
      })`
    },
    shopLink() {
      const gaValue = this.mode === 'confirm' ? 'daicho_rem_url' : 'daicho_reserve_url'
      event(gaValue)
    },
    textCopy() {
      event('daicho_reserve_copy')
      const range = document.createRange()
      range.selectNodeContents(this.$refs.reservation)
      const selection = document.getSelection()
      selection.removeAllRanges()
      selection.addRange(range)
      document.execCommand('copy')
      selection.removeAllRanges()
      this.copyDialog = true
    },
    formatUTC(date) {
      return format(addMinutes(date, parse(date).getTimezoneOffset()), 'YYYYMMDDTHHmmss\\Z')
    },
    onlySeat(course) {
      return !!course.kind && course.kind === 6
    },
    async sendConfirm() {
      event('daicho_nochange')
      if (await this.$store.dispatch('reservation/confirm')) {
        this.confirmed = true
      }
    },
  },
}
</script>

<style lang="postcss" scoped>
.confirmed {
  font-size: 16px;
  padding-top: 8px;
  text-align: center;
  white-space: pre-line;
  @media screen and (min-width: 768px) {
    background: #fff;
    font-size: 18px;
    margin: auto;
    padding: 40px;
    width: 640px;
  }
}

.desc {
  margin-bottom: 16px;
  text-align: center;
  white-space: pre-line;

  @media screen and (min-width: 768px) {
    margin-bottom: 40px;
  }
}

.reservation {
  background: var(--content-bg-color);
  border: 1px solid var(--border-color);
  box-sizing: border-box;
  padding: 32px 16px;
  width: 100%;

  & dt,
  & dd {
    margin: 0 auto;
    max-width: 740px;
  }

  & dt {
    line-height: 16px;
    margin-bottom: 8px;
  }

  & dd {
    color: var(--very-dark-gray-1);
    font-size: 16px;
    line-height: 24px;
  }

  & hr {
    border: none;
    margin: 16px auto;
    max-width: 820px;
  }

  @media screen and (min-width: 768px) {
    border: none;
    padding: 40px 24px;

    & dt {
      font-weight: 600;
    }

    & hr {
      border-bottom: 1px solid var(--border-color);
      margin: 24px auto;
    }

    & .tel {
      display: inline-block;
    }
  }
}

.discount {
  text-decoration: line-through;
}

.confirm {
  align-items: center;
  background: var(--very-pale-yellow);
  display: flex;
  flex-direction: column;
  margin: 16px -16px -16px;
  padding: 32px 16px 24px;
  text-align: center;

  & > :not(:first-child) {
    margin-top: 8px;
  }

  @media screen and (min-width: 768px) {
    margin: 0;
  }
}

.buttons {
  display: flex;
  display: grid;
  grid-gap: 8px;
  grid-template-columns: 1fr 1fr;
  justify-content: center;
  margin-top: 16px;

  @media screen and (min-width: 768px) {
    grid-gap: 24px;
    margin: 48px auto 0;
  }
}

.only-sp {
  @media screen and (min-width: 768px) {
    display: none;
  }
}

.course + .course {
  margin-top: 8px;
}

.tel {
  align-items: center;
  display: flex;
  font-size: 18px;
  justify-content: center;

  & > svg {
    margin-right: 8px;
  }

  @media screen and (max-width: 767px) {
    display: none;
  }
}

.tel-icon {
  fill: var(--brand-color);
  margin-right: 8px;
}

.other {
  white-space: pre-wrap;
  word-wrap: break-word;
}
</style>
